import { useCallback, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { usePrevious } from 'react-use';

import { Button, Icon, Stack } from '@eluve/components';

import { AppointmentTranscriptionInSessionControls } from '../../../components/AppointmentTranscriptionInSessionControls';
import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../hooks/useTranscriptionMachine';

import { AppointmentPatientDetails } from './AppointmentPatientDetails';
import { TranscriptionWaveform } from './TranscriptionWaveform';
import { TranscriptionWidgetTranscript } from './TranscriptionWidgetTranscript';

export const TranscriptionWidget = () => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { tenantId } = useParams<{ tenantId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { send } = useTranscriptionMachineActor();

  const handleDismiss = useCallback(() => {
    send({ type: 'DISMISS_SESSION' });
  }, [send]);

  const sessionAppointmentId = useTranscriptionMachineSelector(
    (state) => state.context.sessionAppointmentId,
  );

  const canDismiss = useTranscriptionMachineSelector((state) =>
    state.can({ type: 'DISMISS_SESSION' }),
  );

  const currentAppointmentPath = sessionAppointmentId
    ? `/organizations/${tenantId}/appointments/${sessionAppointmentId}`
    : null;

  const isOnAppointmentPage =
    currentAppointmentPath &&
    matchPath(currentAppointmentPath, pathname) !== null;
  const wasOnAppointmentPage = usePrevious(isOnAppointmentPage);

  useEffect(() => {
    if (wasOnAppointmentPage && !isOnAppointmentPage && canDismiss) {
      handleDismiss();
    }
  }, [wasOnAppointmentPage, isOnAppointmentPage, canDismiss, handleDismiss]);

  if (!sessionAppointmentId || !currentAppointmentPath || isOnAppointmentPage) {
    return null;
  }

  const openAppointment = () => {
    navigate(currentAppointmentPath);
  };

  return (
    <Draggable
      bounds="parent"
      handle=".move-handle"
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
    >
      <Stack
        ref={nodeRef}
        className="fixed right-5 bottom-5 z-1000 h-[250px] w-[calc(100%-40px)] max-w-[550px] overflow-hidden rounded-lg border bg-bg-primary shadow-xl"
        fullWidth={false}
        gap={0}
      >
        <Stack
          className="bg-bg-tertiary px-4 py-2"
          align="center"
          direction="horizontal"
          justify="between"
        >
          <Stack direction="horizontal" fullWidth={false} gap={2}>
            <Icon
              name="GripVertical"
              size="xl"
              // eslint-disable-next-line better-tailwindcss/no-unknown-classes
              className="move-handle hidden cursor-move md:block"
            />
            <AppointmentPatientDetails appointmentId={sessionAppointmentId} />
          </Stack>
          <Stack direction="horizontal" fullWidth={false} gap={2}>
            <Button onClick={openAppointment} variant="outline">
              <Icon name="Maximize2" size="xl" />
            </Button>
            <Button
              disabled={!canDismiss}
              onClick={handleDismiss}
              variant="outline"
            >
              <Icon name="X" size="xl" />
            </Button>
          </Stack>
        </Stack>
        <TranscriptionWidgetTranscript />
        <Stack
          className="flex-wrap-reverse bg-bg-tertiary p-2"
          align="center"
          direction="horizontal"
          justify="between"
          gap={1}
        >
          <TranscriptionWaveform />
          <AppointmentTranscriptionInSessionControls />
        </Stack>
      </Stack>
    </Draggable>
  );
};
